import {Component, EventEmitter, Inject, Input, LOCALE_ID, OnDestroy, OnInit, Output} from '@angular/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {forkJoin, Observable, of} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {ModalService} from '~shared/components/elements/global-modal/modal.service';
import {ActionButtonType} from '~core/enum';
import {PriceReductionService, StorageService} from '~core/services';
import {TooltipTranslationService} from '~shared/translations';
import {ActionButton, ActionButtonEvent, PriceReduction, ProcessConfiguration, Attribute, Club, Lottery} from '~core/models';
import {TablesTranslations} from '~shared/components/tables/tables-translations.service';
import {EventAdministrationService} from "~features/tixx-select/event-administration.service";

/**
 * Handles price reductions list.
 */
@UntilDestroy()
@Component({
  selector: 'app-price-reductions-list',
  templateUrl: './price-reductions-list.component.html',
})
export class PriceReductionsListComponent implements OnInit, OnDestroy {
  public priceReductionsList: Array<PriceReduction> = [];
  public columns: Array<string> = [];
  public actionButtons: Array<ActionButton> = [];
  public keys: Array<Array<string>> = [];
  public currentProcessConfiguration: ProcessConfiguration;
  public clubAttributes: Array<Attribute> = [];
  public selectedClub: Club;

  public i18nDeleteTitle: string;
  public i18nDeleteParagraph: string;

  @Input() public selectedLottery: Lottery;
  @Input() public isPanelOpened: boolean = true;
  @Output() public isPanelOpenedChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  constructor(
    public tablesTranslations: TablesTranslations,
    public storageService: StorageService,
    private tixxSelectOverviewService: EventAdministrationService,
    private priceReductionService: PriceReductionService,
    public tooltipTranslations: TooltipTranslationService,
    private modalService: ModalService,
    @Inject(LOCALE_ID) public locale: string
  ) {
    this.storageService.castSelectedClub.pipe(untilDestroyed(this)).subscribe((data: Club) => this.selectedClub = data);
    this.priceReductionService.castCurrentPriceReductionList.pipe(untilDestroyed(this))
      .subscribe(data => {
        if (data) {
          for (const list of data) {
            list.attributesString = list.clubAttributes.join(', ');
          }
          this.priceReductionsList = data;
        }
      });
    this.columns.push(
      this.tablesTranslations.i18nName,
      this.tablesTranslations.i18nLimit,
      this.tablesTranslations.i18nClubAttributes,
    );
    this.keys.push(['name', 'limitPerCustomer', 'attributesString']);
  }

  public ngOnInit(): void {
    const calls: Array<undefined | Observable<any>> = [
      this.priceReductionService.get(this.selectedLottery).pipe(untilDestroyed(this), catchError(error => of(null)))
    ];

    forkJoin(calls).subscribe(([priceReduction]) => {
      this.priceReductionService.setCurrentPriceReductionList(priceReduction);
    });

    this.actionButtons.push(
      new ActionButton({
        type: ActionButtonType.EDIT,
        icon: '#esf-icon-edit',
        tooltip: this.tooltipTranslations.i18nEdit,
        disabled: !this.selectedLottery.isDraftMode
      }),
      new ActionButton({
        type: ActionButtonType.DELETE,
        icon: '#esf-icon-trash',
        tooltip: this.tooltipTranslations.i18nDelete,
        disabled: !this.selectedLottery.isDraftMode
      })
    );
    this.i18nDeleteTitle = $localize`:@@tixxSelect.modalTitle.deletePriceReduction:Delete Price Reduction?`;
    this.i18nDeleteParagraph = $localize`:@@tixxSelect.modalParagraph.deletePriceReduction:When deleting this reduction the information will be lost and the reduction will be removed from all assigned price categories. Would you like to delete the reduction?`;

  }

  public ngOnDestroy(): void {
  }

  /**
   * Open the create price reduction modal
   */
  public createPriceReduction(): void {
    this.tixxSelectOverviewService.toggleAllModal(false);
    this.tixxSelectOverviewService.toggleCreatePriceReductionsModal(true);
  }

  /**
   * Open the create price reduction modal
   */
  public editPriceReduction(priceCategory: any): any {
    this.priceReductionService.setCurrentPriceReduction(priceCategory);
    this.tixxSelectOverviewService.toggleAllModal(false);
    this.tixxSelectOverviewService.toggleEditPriceReductionsModal(true);
  }

  public async showDeleteModal(id: string): Promise<any> {
    this.modalService.setModalOptions(this.i18nDeleteTitle, this.i18nDeleteParagraph);
    const promise = await new Promise<boolean>((resolve, reject) => this.modalService.openModal(resolve, reject));
    if (promise) {
      this.deletePriceReduction(id);
    }
  }

  public deletePriceReduction(id: string): void {
    this.priceReductionService.delete(id).pipe(untilDestroyed(this))
      .subscribe(() => {
        this.priceReductionService.deletePriceReductionList(id);
        this.modalService.setStatus(false);
      });
  }

  /**
   * Checks which action button was clicked
   */
  public actionButtonClicked(actionButton: ActionButtonEvent<any>): void {
    switch (actionButton.type) {
      case ActionButtonType.EDIT:
        this.editPriceReduction(actionButton.data);
        break;
      case ActionButtonType.DELETE:
        this.showDeleteModal(actionButton.data.id);
        break;
      default:
    }
  }
}
